import { useAsyncData, useState } from '#imports'
import { computed, onUnmounted } from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import type { RecommendationAlgoliaCarouselProps } from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { useAlgoliaRecommendations } from '@backmarket/nuxt-layer-recommendation/useAlgoliaRecommendations'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import {
  getBrazeInstance,
  whenBrazeIsReady,
} from '@backmarket/nuxt-module-tracking/getBrazeInstance'
import { tryCatch } from '@backmarket/utils/function/tryCatch'

import { useSearchProducts } from '../../../products/useSearchProducts'
import type { RefinedProductsBlockProps } from '../../RefinedProducts/RefinedProducts.vue'
import {
  type BrazeContentCard,
  type ContentCard,
  type SupportedFallbackTypes,
  mergeCardsByPriority,
  reindexTrackingPositions,
  transformCMSProductBlocks,
  transformContentCards,
} from '../PersonalizedFeed.utils'

const MAX_BRAZE_CARDS_REFRESH_MS = 5_000

function rejectIfNotResolvedAfter(
  reject: (reason: Error) => void,
  { timeout }: { timeout: number },
) {
  setTimeout(() => {
    reject(
      new Error(
        `Refreshing Braze content cards took too long. Forcefully timing out after ${timeout}ms and displaying the fallback cards`,
      ),
    )
  }, timeout)
}

async function getBrazeCards({
  triggerRefresh,
}: {
  triggerRefresh: boolean
}): Promise<BrazeContentCard[]> {
  const instance = getBrazeInstance()

  if (!instance) {
    return []
  }
  const cachedCards = instance.getCachedContentCards()?.cards || []

  if (cachedCards.length > 0) {
    // make sure to refresh the cached cards to have fresh data on the next page load
    // TODO: discuss on a cache TTL for which we can keep cached cards
    instance.requestContentCardsRefresh()

    return cachedCards
  }

  const {
    promise: contentCardsPromise,
    resolve,
    reject,
  } = promiseWithResolvers<BrazeContentCard[]>()

  instance.subscribeToContentCardsUpdates((updates) => {
    resolve(updates.cards)
  })

  // this allows displaying the fallback cards instead of indefinite loading state if the Braze content cards refresh takes too long
  rejectIfNotResolvedAfter(reject, { timeout: MAX_BRAZE_CARDS_REFRESH_MS })

  // this ensures that only one PersonalizedFeed block triggers a refresh at the same time
  if (triggerRefresh) {
    instance.requestContentCardsRefresh(
      () => {
        // successful callback does nothing
      },
      () => {
        reject(
          new Error(
            'An unexpected error occurred while refreshing Braze content cards. This is most likely a due to a network issue or a tracker blocker',
          ),
        )
      },
    )
    instance.requestImmediateDataFlush()
  }

  return contentCardsPromise
}

export function usePersonalizedFeed({
  feed,
  productQuery,
  fallbackV2,
  additionalRecommendations,
  title,
  blockPosition,
}: {
  feed: string
  productQuery: PersonalizedFeedApiProps['productQuery']
  fallbackV2: PersonalizedFeedApiProps['fallbackV2']
  additionalRecommendations: PersonalizedFeedApiProps['additionalRecommendations']
  blockPosition: number
  title: string
}) {
  const brazeRefreshTriggered = useState('braze-refresh-triggered', () => false)

  onUnmounted(() => {
    brazeRefreshTriggered.value = false
  })

  async function returnFallbackCardsResult(): Promise<ContentCard[]> {
    const fallbackContentCards = transformCMSProductBlocks({
      items: fallbackV2,
      blockPosition,
      title,
      source: 'fallback',
    })

    const successfulFallbackCards =
      await fetchContentCardsDataAndKeepOnlySuccessful(fallbackContentCards)

    return reindexTrackingPositions(successfulFallbackCards)
  }

  const { data, pending, error } = useAsyncData(
    getAsyncDataKey('personalized-feed', { feed }),
    async (): Promise<ContentCard[]> => {
      const isEnabled = await whenBrazeIsReady()

      if (!isEnabled) return await returnFallbackCardsResult()

      const doTriggerRefresh = !brazeRefreshTriggered.value

      if (doTriggerRefresh) brazeRefreshTriggered.value = true

      const [brazeResult, additionalResult] = await Promise.all([
        tryCatch(getBrazeCards({ triggerRefresh: doTriggerRefresh })),
        tryCatch(
          (async () => {
            const additionalContentCards = transformCMSProductBlocks({
              items: additionalRecommendations,
              blockPosition,
              title,
              source: 'additionalRecommendation',
            })

            return fetchContentCardsDataAndKeepOnlySuccessful(
              additionalContentCards,
            )
          })(),
        ),
      ])

      const [brazeCards, brazeCardsError] = brazeResult

      if (brazeCardsError) return await returnFallbackCardsResult()

      const cards = transformContentCards({
        cards: brazeCards,
        feed,
        productQuery,
        blockPosition,
      })

      const successfulBrazeCards =
        await fetchContentCardsDataAndKeepOnlySuccessful(cards)

      if (successfulBrazeCards.length === 0) {
        return await returnFallbackCardsResult()
      }

      const [additionalCards] = additionalResult
      const successfulAdditionalCards = additionalCards || []

      const mixed = mergeCardsByPriority(
        successfulBrazeCards,
        successfulAdditionalCards,
      )

      return reindexTrackingPositions(mixed)
    },
    {
      server: false,
      immediate: true,
    },
  )

  const cards = computed(() => data.value || [])

  return {
    cards,
    pending,
    error,
  }
}

async function fetchContentCardsDataAndKeepOnlySuccessful(
  cards: ContentCard[],
): Promise<ContentCard[]> {
  const useAsyncDataList: Array<{
    successfulRequestPromise: Promise<boolean>
  }> = cards.map((card) => {
    const props =
      'props' in card.props
        ? (card.props as SupportedFallbackTypes).props
        : card.props

    if ('productQuery' in props) {
      const { productQuery: pq, sortFn } = props as RefinedProductsBlockProps

      const asyncDataPromise = useSearchProducts(
        {
          productQuery: pq,
        },
        {
          immediate: true,
          server: false,
          sort: sortFn,
        },
      )

      return {
        // promise that resolves to true once the async data is fetched and is successful
        successfulRequestPromise: asyncDataPromise.then(
          (asyncData) =>
            !!(asyncData.data.value && asyncData.data.value.length > 0),
        ),
        asyncDataPromise,
      }
    }
    if ('recommendationQuery' in props) {
      const { recommendationQuery } =
        props as RecommendationAlgoliaCarouselProps

      const asyncDataPromise = useAlgoliaRecommendations(
        recommendationQuery.objectIds,
        recommendationQuery.model,
        recommendationQuery.options,
        {
          server: false,
          cache: true,
        },
      )

      return {
        asyncDataPromise,
        // promise that resolves to true once the async data is fetched and is successful
        successfulRequestPromise: asyncDataPromise.then(
          (asyncData) =>
            !!(asyncData.data.value && asyncData.data.value?.length > 0),
        ),
      }
    }

    // for other card types, there is no async data to fetch, so we automatically resolve to true
    return {
      successfulRequestPromise: Promise.resolve(true),
    }
  })

  const asyncDataResults = await Promise.all(
    useAsyncDataList.map(async ({ successfulRequestPromise }, index) => {
      const successful = await successfulRequestPromise

      return {
        successful,
        index,
      }
    }),
  )

  const successfulCardIndexes = asyncDataResults
    .filter((result) => result.successful)
    .map((result) => result.index)

  return cards.filter((_card, index) => successfulCardIndexes.includes(index))
}

// Polyfill for Promise.withResolvers taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/withResolvers#description
// Promise.withResolvers is mostly compatible on 2024+ browser versions
function promiseWithResolvers<T>() {
  let resolve!: (v: T) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let reject!: (reason?: any) => void

  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })

  return {
    promise,
    resolve,
    reject,
  }
}
